/**
 * Weapon tuning (IMPLEMENTATION_PLAN.md D1–D3).
 *
 * Every number the feel gate iterates on lives here, so tuning weapon satisfaction is
 * data-only work — never a code change. The comments record *why* a value is what it is,
 * because a bare number tells the next pass nothing about what it was trading against.
 */

export interface AutocannonSpec {
  /** m/s at the muzzle. 30 mm naval autocannon territory. */
  muzzleVelocity: number;
  /**
   * Quadratic drag coefficient, 1/m. Tuned so a 4 km shot takes ~5.2 s and drops
   * visibly — the arc is the signature image, so drag is a feel parameter, not physics
   * pedantry.
   */
  dragK: number;
  /** Base dispersion cone, radians (1.5 mrad). */
  dispersionBase: number;
  /** Extra dispersion per continuous second of fire, radians. */
  dispersionGrowth: number;
  /** Fraction of accumulated heat shed per second when not firing. */
  dispersionRecovery: number;
  /** Rounds per minute. */
  rpm: number;
  /**
   * Every Nth round is a tracer.
   *
   * Fewer means a denser burning line and a much easier read on where the stream is
   * actually going; more means the arc is implied rather than drawn. This is a legibility
   * dial as much as an authenticity one.
   */
  tracerEvery: number;
  magazine: number;
  /** Seconds to reload from reserve. */
  reloadSeconds: number;
  /** Base damage per round before the armour matrix. */
  damage: number;
  /** Scope camera kick per shot, radians. */
  recoilScope: number;
  /** Grazing angle below which a water hit *can* skip, radians. */
  ricochetGrazeAngle: number;
  /** Probability that a shallow water hit skips rather than sinking. */
  ricochetChance: number;
  /** Most times one round may skip. */
  ricochetMaxSkips: number;
  /** Rounds despawn beyond this range, metres. */
  maxRange: number;
}

export const AUTOCANNON: AutocannonSpec = {
  muzzleVelocity: 1000,
  // Chosen with the ballistics integrator, not analytically: this is the value that puts
  // a 4 km round in the air for ~5.2 s with roughly 90 m of drop.
  dragK: 0.000045,
  /*
   * 2.5 -> 1.5 mrad: Adam reported skiffs impossible to hit at 3 km, and he was right.
   *
   * A fixed angular cone means the miss AREA grows with the square of range, so the gun
   * fell off a cliff somewhere past 2 km. Measured against the sim's own capsule (three
   * spheres of radius max(beam,height)/2 along the centreline) at 3 km in calm weather,
   * bow-on — which is how a skiff closes, and therefore the case that matters:
   *
   *   2.5 mrad, old 30% hull   cone 18.9 m across   3.2% per round   12.3 s to two hits
   *   2.5 mrad, 50% hull       cone 18.9 m across   4.3% per round    9.4 s
   *   1.5 mrad, 50% hull       cone 12.9 m across   9.2% per round    4.4 s
   *
   * The hull is 3.9 m wide. No plausible size fixes a cone five times wider than the
   * target — reaching 9% by growing the skiff alone would need a 15 m beast. The cone
   * was the term to move. At 1.5 mrad a three-second burst at 3 km lands more than one
   * round, so the player gets feedback instead of a lottery; inside 1 km it makes an
   * already-lethal gun quicker rather than different in kind. `rifledBarrel` still
   * earns its place, taking the working cone to ~1.05 mrad.
   */
  dispersionBase: 0.0015,
  dispersionGrowth: 0.0004,
  dispersionRecovery: 0.67,
  rpm: 300,
  tracerEvery: 5,
  magazine: 1800,
  reloadSeconds: 8,
  damage: 14,
  recoilScope: 0.0003,
  /*
   * A shallow round *may* skip, once, sometimes.
   *
   * This used to be a certainty rather than a chance, and unbounded: every round from
   * 1 km out skipped eleven times, so four shots produced dozens of impacts marching away
   * across the sea. The bounce also bled horizontal speed faster than vertical, which
   * made each hop shallower than the last and guaranteed it would trigger again — the
   * opposite of how a stone behaves.
   */
  ricochetGrazeAngle: 0.14,
  ricochetChance: 0.28,
  ricochetMaxSkips: 1,
  maxRange: 8000,
};

export interface TorpedoSpec {
  launchSpeed: number;
  /** Running depth once settled, metres below the surface. */
  runDepth: number;
  /** Speed at the start and end of the acceleration phase, m/s. */
  speedStart: number;
  speedMax: number;
  /** Seconds to go from speedStart to speedMax — the "machine building power" beat. */
  accelSeconds: number;
  /** Gyro turn rate, radians per second. */
  turnRate: number;
  /** Metres before the warhead arms. */
  armDistance: number;
  /** Proximity fuse radius, metres. */
  fuseRadius: number;
  damage: number;
  /** Maximum run before it sinks inert, metres. */
  maxRun: number;
  /** Seconds between launches (tube interlock). */
  tubeCycleSeconds: number;
  /**
   * Persistent gyro bias, radians per second.
   *
   * A real fish does not run on a ruled line — the gyro drifts and the screws torque it
   * off heading. Each torpedo gets its own bias, derived from its id so a replay is
   * identical, which means no two shots curve the same way. Small enough that a locked
   * weapon still corrects onto its target; large enough that the wake is an arc rather
   * than a straight edge drawn across the sea.
   */
  gyroDrift: number;
}

export const TORPEDO: TorpedoSpec = {
  launchSpeed: 9,
  runDepth: 4,
  /*
   * Faster than the plan's 5 -> 30 over twelve seconds, on Adam's direction after playing
   * it: "it should move fast".
   *
   * The plan names that curve as a feel target rather than a physical fact, and feel is
   * his call. The character it was buying — a machine visibly building power that you
   * cannot recall — survives, because the weapon still starts well under speed and takes
   * five seconds to reach it. What is gone is the first several seconds of crawl, which
   * on a 900 m shot read as a bug rather than as tension.
   */
  speedStart: 14,
  speedMax: 44,
  accelSeconds: 5,
  turnRate: 0.052,
  /*
   * Was 150. Adam's lock hit-curve (TORPEDO_LOCK) gives real odds down to 100 m and
   * zero below, so the physical arming interlock moves to match — the curve is now the
   * authority on what close-in shots are worth.
   */
  armDistance: 100,
  fuseRadius: 8,
  damage: 400,
  maxRun: 4000,
  tubeCycleSeconds: 8,
  gyroDrift: 0.0065,
};

export interface DepthChargeSpec {
  minRange: number;
  maxRange: number;
  /**
   * Speed the charge leaves the tube at, m/s.
   *
   * This is what makes `maxRange` reachable, and for a long time it was not a tuning
   * number at all — it sat as a default argument on `lobVelocity`, and the one call site
   * never passed it. That is how the rails upgrade below came to be broken.
   */
  launchSpeed: number;
  /** Envelope and launch speed with the `projectorRails` upgrade fitted. */
  railsMaxRange: number;
  railsLaunchSpeed: number;
  /** Sink rate once in the water, m/s. */
  sinkRate: number;
  /** Selectable fuse depths, metres. */
  fuseDepths: readonly number[];
  /** Full damage inside this radius, metres. */
  lethalRadius: number;
  /** Damage falls to zero at this radius. */
  falloffRadius: number;
  damage: number;
  /** Seconds between lobs. */
  cycleSeconds: number;
  /** Seconds to swap a spent projector magazine. */
  magSwapSeconds: number;
}

export const DEPTH_CHARGE: DepthChargeSpec = {
  minRange: 150,
  maxRange: 1200,
  /*
   * A lob has a ceiling, and both envelopes are set under theirs.
   *
   * At a fixed launch speed there is a furthest point the charge can reach at any
   * elevation — from the projector's plinth at 84.91 m,
   *
   *     R = (v²/g)·√(1 + 2gh/v²)
   *
   * which is 1315 m at 110 m/s. Past it `lobVelocity`'s discriminant goes negative and it
   * bails out to a 45-degree throw, so the charge lands at the ceiling wherever it was
   * aimed. The 1200 m envelope sits ~10% under that, which is the margin that keeps every
   * accepted shot a solved one.
   *
   * The rails upgrade used to widen the envelope by 300 m and nothing else, which put its
   * top 185 m past the ceiling: a $90,000 purchase whose new range silently landed short —
   * exactly the dud D3 says this weapon never throws. Extended rails now do what the name
   * says and throw harder, at the same 10% margin: 124 m/s reaches 1650 m, and the
   * envelope opens to 1500.
   */
  launchSpeed: 110,
  railsMaxRange: 1500,
  railsLaunchSpeed: 124,
  sinkRate: 3,
  fuseDepths: [10, 25, 45],
  /*
   * The lethal core fills the column the player can actually see (Adam, 2026-07-29).
   *
   * He watched a mini-sub sit inside a depth-charge burst and come out untouched, and
   * called it cheating. He was right, and the cause was not the damage being too small —
   * it was the picture and the model disagreeing. The FX draws the burst at
   * `falloffRadius`, so the visible column has always been 40 m across, while full damage
   * stopped at 15: the outer 62% of a blast that looks lethal did next to nothing.
   *
   * So the core grew to meet the picture rather than the picture shrinking to meet the
   * core — the explosion is the most satisfying thing the projector does and is not to be
   * touched. 15 -> 25 m lethal, falloff unchanged at 40, which is the edge of the column.
   * Anything visibly inside the blast now dies.
   *
   * His first instinct was to double both radii, and that was measured before being set
   * aside: at 80 m the blast outgrows the depths it is meant to discriminate between, and
   * the fuse selector collapses from decisive to marginal — a boat circling at 10 m died
   * to all three settings within a few metres of each other. This keeps the selector
   * whole: mismatching the fuse still costs placement margin, and 45 m still cannot reach
   * a shallow boat at all.
   */
  lethalRadius: 25,
  falloffRadius: 40,
  damage: 350,
  cycleSeconds: 4,
  magSwapSeconds: 12,
};

/**
 * The projector's envelope and the speed that backs it, for the fit the player has.
 *
 * Range and launch speed have to move together or the weapon throws duds, so nothing
 * reads one without the other. Both the firing check and the HUD's envelope marker come
 * through here.
 */
export function projectorEnvelope(hasRails: boolean): {
  minRange: number;
  maxRange: number;
  launchSpeed: number;
} {
  return {
    minRange: DEPTH_CHARGE.minRange,
    maxRange: hasRails ? DEPTH_CHARGE.railsMaxRange : DEPTH_CHARGE.maxRange,
    launchSpeed: hasRails ? DEPTH_CHARGE.railsLaunchSpeed : DEPTH_CHARGE.launchSpeed,
  };
}

/**
 * Where the gun physically sits on the rig. Ballistic solutions and the scope's zero are
 * computed from here, not from the eye point — the parallax between the two is most
 * visible under 500 m and the plan requires it be honest.
 */
export const GUN_MOUNT = {
  /**
   * Metres, world space, relative to the rig at the origin.
   *
   * 3.5 m west of the stance — the player's left hand when facing north, which is the
   * default heading. Adam's placement: beside the player on the observation platform,
   * not in front of them.
   */
  x: -5.5,
  /**
   * Trunnion height, on a pad raised one metre off the 103.3 deck. That puts the
   * barrel's axis within a few centimetres of the 105 eye line — the gun stands beside
   * you at head height rather than being something you look down on.
   */
  y: 105.3,
  /**
   * Level with the stance, a touch inboard of the rail at 4.35 so the pad sits wholly
   * on the platform. Was (6.5, 99, 6.0): a bare ballistic origin six metres under the
   * deck, which the player could never see. See render/rig/weaponMounts.ts, which
   * builds the visible mount from this constant.
   */
  z: 3.0,
} as const;

/**
 * The torpedo tubes, low on the jacket near the waterline.
 *
 * Deliberately not up beside the gun. A tube 99 m in the air would have the weapon
 * spending its first second falling rather than running, and the launch would read as a
 * bomb drop instead of the machine sliding into the water that plan D2 asks for.
 */
export const TORPEDO_MOUNT = {
  x: -4.0,
  y: 11.0,
  z: 9.5,
} as const;

/**
 * The depth-charge projector, on the north-west corner of the crane tower's lowest ring
 * gallery — the walkway that runs around the tower at MAIN_DECK_Y + 6, which after the
 * tower's 18 m drop puts its deck at 84.46.
 *
 * Adam marked this spot on a screenshot after a first attempt used the crane's own 8 m
 * base platform (x 5..13, z 15.5..23.5, top 78.8). Both answer to "the crane tower's
 * lower standing platform", but only this one is the place he meant, and the geometry
 * says why: this gallery sits 20 m below the eye at 29 degrees of depression, so the
 * sightline clears the observation rail by a few centimetres and the mount is visible
 * from where the player stands. The base platform is 26 m down and the rail cuts it off
 * completely.
 */
export const PROJECTOR_MOUNT = {
  x: 5.6,
  /** Plinth top; the tube pivots here. */
  y: 84.91,
  z: 39.0,
} as const;

/** Tubes loaded at once. A salvo is two; the third shot waits on the racks. */
export const TORPEDO_TUBES = 2;

/** Charges in the projector before a magazine swap is forced. */
export const PROJECTOR_MAGAZINE = 4;

/** Seconds to bring a fresh torpedo up from the racks into a spent tube. */
export const TORPEDO_RACK_RELOAD = 30;

/**
 * The autocannon's report.
 *
 * The recording is one round: a sharp crack followed by its echo rolling away over open
 * water, about 1.4 s of tail before it dies. So it plays once per round, and at 300 rpm
 * the tails deliberately overlap — that pile-up of echoes *is* what sustained fire over
 * the sea sounds like.
 *
 * (Recorded here because I got this wrong first time. The envelope holds near-full level
 * for 0.9 s, which I read as a burst; it is actually reverb. Autocorrelating the envelope
 * shows no periodic peak — a burst would spike at the shot interval — so the data never
 * supported the burst reading either.)
 */
export const AUTOCANNON_SOUND = {
  /**
   * Two takes of the same gun, alternated round by round.
   *
   * They are nearly identical but not quite, and that difference is the whole point: a
   * single sample repeated five times a second reads as a machine playing a file. Two
   * takes in rotation, each with its own small pitch offset, reads as a gun.
   */
  urls: ['/audio/30mm.wav', '/audio/30mm-2.wav'],
  /**
   * Per-round level.
   *
   * At 300 rpm a 3.2 s recording means about sixteen tails sounding at once in steady
   * fire, so this sits well below unity and the master limiter catches the rest. It is
   * the one value here that wants an ear rather than a measurement.
   */
  gain: 0.6,
  /**
   * Playback-rate jitter, ± this fraction. Identical repeats comb-filter against their
   * own echoes and start to sound synthetic; a few percent of variation breaks that up
   * and reads as barrel-to-barrel inconsistency.
   */
  pitchJitter: 0.035,
  /**
   * No voice cap, deliberately.
   *
   * Capping meant fading the oldest tail early, which is trimming the echo — the part of
   * the recording that carries the sense of open water. Every round plays its full 3.2 s.
   * The count is self-limiting anyway: fire rate times sample length, about sixteen.
   */

  /**
   * How much each already-ringing voice is pushed down when a new round fires.
   *
   * Sixteen overlapping echoes sum into a bed loud enough to bury the very transients
   * that tell you the gun is firing — the shots measured exactly on time at 200 ms apart,
   * but they *sounded* slower than they looked, because each new crack arrived into the
   * wash of its fifteen predecessors. Ducking what is already sounding puts the newest
   * crack on top without shortening anything: after five rounds an old tail is at 0.68^5,
   * about a seventh, and still running its full length underneath.
   *
   * It also gives sustained fire and single shots different characters, which is right —
   * hold the trigger and you get tight cracks over a receding wash; stop, and the last
   * round's echo rolls out across the water at full level with nothing ducking it.
   */
  duckPerShot: 0.68,
  /** Seconds over which that duck is applied. Fast enough to clear the incoming crack. */
  duckTime: 0.035,
} as const;

/**
 * The tube launch, as one designed recording.
 *
 * Adam layered this from four takes — tube thump, air charge, entry splash, tail — mixed
 * at fixed offsets (0 / 0.8 / 2.6 / 4.1 s) into a single 9.4 s file, summed with the
 * peaks intact and the whole brought down to −1 dBFS. It replaces the synthesised
 * thump-hiss-splash, which stays in the engine as the missing-file fallback.
 */
export const TORPEDO_LAUNCH_SOUND = {
  url: '/audio/torpedo-launch.wav',
  /**
   * Below the horn, above a single gun round. A launch happens a handful of times a
   * shift and the tube is right under the player, but the drama of the weapon is the
   * wait after the splash — the recording's own long tail carries that, so it does not
   * need to be loud to be big.
   */
  gain: 0.7,
} as const;

/**
 * The water a running torpedo moves, layered under the synthesised screw whine.
 *
 * Adam's take, prepared as a seamless 4.5 s loop (the last half-second crossfaded over
 * the first, since a run's length varies shot to shot). The two are one voice to the
 * ear: the whine is the machine, this is the sea reacting to it, and both ride the same
 * launch ramp, distance fade and stop.
 */
export const TORPEDO_RUN_SOUND = {
  url: '/audio/torpedo-run.wav',
  /**
   * Adam's ratio: 25% over the whine's 0.13 close in — the water is the body of the
   * sound, the screw the detail — but it fades 25% quicker with distance, so the far
   * half of a run belongs to the screw alone. Which is physical, as it happens: the
   * broadband rush is the first thing water absorbs, the low tone the last.
   */
  gain: 0.1625,
} as const;

/**
 * The torpedo's aim-lock (Adam's redesign, 2026-07-28 — replaces Q/E cycling for this
 * weapon).
 *
 * Hold the crosshair on a hostile — generously; the cone includes the hull's own
 * angular width — for `acquireSeconds`, and the fish locks. It will not fire unlocked.
 * The outcome is decided at launch from range by `hitCurve`: past `guaranteedRange` a
 * locked target is doomed, close in the odds collapse — minimum range expressed as
 * worsening odds rather than a refusal. Speedboats cap at `speedboatCap` even far out.
 * Divers and the boss are never rigged either way; they keep the physical fuse and
 * earn their escapes honestly.
 */
/**
 * The autocannon's acquisition, on Adam's direction (2026-07-29).
 *
 * The lead pip used to require an explicit selection — tap the hull or cycle onto it with
 * Q/E — which meant a player who had already put the crosshair on a boat still had to say
 * so a second time before the game would tell them where to aim. The gun now acquires the
 * way the torpedo does: point at it, hold it, and the solution arrives.
 *
 * Same 1.2 s dwell as the torpedo, his call, so the two weapons feel like one instrument
 * rather than two. The cone is the only place they differ on purpose — see `coneDeg`.
 */
/**
 * Shooting a torpedo out of the water with the 30 mm (Adam, 2026-07-29).
 *
 * A mini-sub's torpedo runs at 5 m/s from about 950 m out, so it is in the water for
 * minutes — there is a long, obvious window in which the player can see the thing coming
 * and do something about it. That window existed already and there was nothing to do with
 * it: shells and torpedoes passed through each other.
 *
 * `radius` is a hit *volume* around the shell, not the torpedo's size. The gun scatters
 * about 1.5 mrad, which at 900 m is a cone some 1.4 m across, so a radius near the
 * warhead's own dimensions would make interception a lottery decided by the dispersion
 * roll rather than by aim. Eight metres is the value at which a burst held on a visible
 * torpedo track connects in a second or two, and a burst sprayed near it does not.
 */
export const TORPEDO_INTERCEPT = {
  /** Miss distance at which a shell kills a torpedo, metres. */
  radius: 8,
  /**
   * Only the gun. The projector's charges already reach underwater on their own terms and
   * a torpedo detonating a torpedo would be a chain reaction nobody asked for.
   */
  weapon: 'shell',
} as const;

export const GUN_LOCK = {
  /** Adam: the same dwell as the fish. */
  acquireSeconds: 1.2,
  /**
   * Aim cone half-angle, degrees, on top of the hull's own presented half-width.
   *
   * Wider than the torpedo's 0.35. That number was cut hard because picking the wrong
   * boat wastes a shot the player has four of; a lead pip that resolves onto the nearer of
   * two hulls costs nothing but a glance, and the tighter cone would make the pip flicker
   * out every time the sight drifted a few tenths of a degree off a distant skiff. Still
   * far inside the 8x sight radius of 4.13 deg, so the crosshair has to be on the boat.
   */
  coneDeg: 0.6,
  /** Fraction of the presented silhouette's angular half-width that counts. */
  hullWidthFactor: 0.75,
  /** Once acquired, the pip survives inside this multiple of the acquire cone. */
  holdConeFactor: 2,
  /** Wandering off drains progress this many times faster than holding on builds it. */
  offTargetDrain: 2.5,
  /**
   * No solution past where the gun is an argument.
   *
   * The scripts stage every engagement inside 6 km and the fights themselves inside 4;
   * beyond that the shell is airborne so long, and the cone so wide against the hull, that
   * a pip would be promising something the gun cannot deliver.
   */
  maxRange: 4000,
} as const;

export const TORPEDO_LOCK = {
  acquireSeconds: 1.2,
  /**
   * Aim cone half-angle, degrees, added to the target hull's own angular half-width.
   *
   * 2.2 -> 1.4 -> 0.35. The first two cuts trimmed this margin while the hull term was
   * still `length` at any aspect, so they were trimming the smaller of the two problems:
   * at 2 km a corvette's cone was 2.39 deg against a scope half-FOV of 4.13, i.e. over
   * half the sight radius, and at 3 km this fixed term alone was 79% of the cone. With
   * the hull term now the real presented silhouette, the margin only has to cover the
   * wobble of holding an 8x sight for 1.2 s — a bow-on FAC at 2 km comes to 0.46 deg,
   * about 11% of the sight radius, so the crosshair has to be on the boat.
   */
  coneDeg: 0.35,
  /** Fraction of the presented silhouette's angular half-width that counts. */
  hullWidthFactor: 0.75,
  /** Once locked, the lock survives inside this multiple of the acquire cone. */
  holdConeFactor: 2,
  /** Wandering off drains progress this many times faster than holding on builds it. */
  offTargetDrain: 2.5,
  /** No lock beyond the fish's own fuel. */
  maxRange: TORPEDO.maxRun,
  /** Beyond this range a locked shot is certain (speedboats aside). */
  guaranteedRange: 650,
  /** [range m, chance] points, linearly interpolated between; zero below the first. */
  hitCurve: [[100, 0.1], [200, 0.2], [300, 0.3], [400, 0.5], [500, 0.8], [650, 1]],
  /** Hulls quick enough that even a far shot stays a gamble. */
  speedboats: ['skiff', 'fac'],
  speedboatCap: 0.75,
  /** Kinds whose fate is never rolled: the sub can dive, the boss is the boss. */
  unriggable: ['minisub', 'tentacle', 'kraken'],
  /** Steering authority used to enforce a rolled outcome, rad/s. */
  riggedTurnRate: 0.5,
  /** A rolled miss tracks a point this far astern of the hull, metres — a near thing. */
  missOffset: 22,
} as const;

/**
 * The torpedo striking a hull — two owner takes summed into one 4 s detonation.
 *
 * Played at the game's speed of sound (twice physical — see the engine's
 * SPEED_OF_SOUND): the engine schedules it by distance after the impact the player just
 * watched, so a 2 km kill flashes silently and the report rolls in three seconds later.
 * That gap is not a defect — it is most of what sells the size of the water, and the
 * reason the launch is forbidden from spending it early.
 */
export const TORPEDO_HIT_SOUND = {
  url: '/audio/torpedo-hit.wav',
  /** At the rail. Distance attenuation scales it down from here. */
  gain: 0.85,
} as const;

/**
 * The depth charge going off, in two recordings that land on the two beats the player
 * watches: the sea heaving up, then the column breaking through it.
 *
 * Both ride the game's speed of sound (686 m/s, twice physical) on top of their own
 * beat offset, so a charge at 900 m is seen to lift at 0.3 s and heard to lift at
 * 1.6 s. That gap is the point — this weapon is used at range, and the delay is what
 * says how far away the thing you just did happened.
 */
export const DEPTH_CHARGE_SOUND = {
  /** The projector throwing the charge. Adam's take, 2026-07-30. */
  launchUrl: '/audio/depth-charge-firing.wav',
  /** The charge entering the water, seconds after the lob — before it starts sinking. */
  splashUrl: '/audio/charge-splash.wav',
  liftUrl: '/audio/depth-charge-lift.wav',
  columnUrl: '/audio/depth-charge-column.wav',
  /**
   * The lift is the muffled underwater half — felt more than heard, and quieter than
   * the eruption that follows it.
   */
  liftGain: 0.6,
  columnGain: 0.9,
  /**
   * The entry splash: a real but small event next to the explosion, and the only one of
   * the three the player hears while the weapon is still theirs to think about.
   */
  splashGain: 0.55,
  /**
   * The launch is on the rig with the player, not out on the water, so it plays
   * near-field at a fixed level — no distance falloff and no travel delay, the same
   * treatment the torpedo tube gets.
   *
   * 0.8 was set against the previous take; the firing take is a different animal and the
   * number had to follow it. Measured across the first 500 ms — the report itself — the
   * new recording sits 8.7 dB lower in RMS while peaking 1 dB higher (0.0 dBFS against
   * −1.0): a sharper crack carrying much less body. At the old gain it read thin. This
   * gives back about half the difference, which is as far as gain alone can go — the
   * source is already at full scale, and the master limiter (−6 dB, 12:1) turns anything
   * more into the whole mix ducking under every lob.
   */
  launchGain: 1.3,
  /**
   * The splash carries like an ordinary impact rather than like the burst, so it takes
   * the shorter reference — at 2 km it should be a distant slap, not a boom.
   */
  splashReferenceRange: 900,
  /**
   * Reference distance for the inverse falloff, metres. Longer than the hull-strike
   * model's 900 because this is a far bigger event: several hundred kilos of explosive
   * carries across water that a shell hitting a hull does not.
   */
  referenceRange: 1400,
} as const;

/**
 * A 30 mm round going into the water, as two owner recordings played interchangeably.
 *
 * Audible only inside 650 m, on Adam's curve. That is a deliberate range gate rather
 * than a falloff that merely gets quiet: at 4 km a stream of individual splashes is
 * neither plausible nor useful, and the clustered synthesised report already covers the
 * far water. Inside 650 m the splashes are the feedback that says where the burst is
 * actually landing, which is how the player walks fire onto a target.
 */
export const WATER_HIT_SOUND = {
  /** Two takes, alternated strictly so consecutive rounds never repeat the same file. */
  urls: ['/audio/water-hit-1.wav', '/audio/water-hit-2.wav'],
  /** Beyond this nothing plays at all. */
  maxRange: 650,
  /**
   * [range in metres, level] — Adam's numbers, linear between the points, flat at full
   * level inside the first and silent past the last.
   */
  curve: [
    [150, 1], [250, 0.8], [400, 0.5], [500, 0.25], [600, 0.15], [650, 0],
  ],
  /** Master level for the whole family, applied on top of the curve. */
  gain: 0.85,
  /** Playback-rate jitter, ± this fraction, so a long burst does not comb-filter. */
  pitchJitter: 0.06,
  /**
   * Random scatter on arrival, seconds — the same fix the hull strikes needed. Rounds
   * landing together share a distance and so shared a travel delay exactly, stacking in
   * phase into one loud splash instead of several.
   */
  arrivalScatter: 0.09,
  /**
   * Concurrent splash voices. At 300 rpm inside the audible band a two-second sample
   * would otherwise stack ten deep and turn the sea into white noise; past this the
   * oldest voice is faded out rather than the newest being dropped, so the most recent
   * rounds are always the ones you hear.
   */
  maxVoices: 9,
} as const;

/**
 * Adam's audibility curve for a round hitting the water, read at `range` metres.
 *
 * Exported so a test can hold the numbers to the letter — they are a creative
 * specification, not a tuning convenience.
 */
export function waterHitLevel(range: number): number {
  const curve = WATER_HIT_SOUND.curve;
  const first = curve[0] as readonly [number, number];
  if (range <= first[0]) return first[1];
  for (let i = 1; i < curve.length; i++) {
    const [r1, l1] = curve[i] as readonly [number, number];
    const [r0, l0] = curve[i - 1] as readonly [number, number];
    if (range <= r1) return l0 + ((range - r0) / (r1 - r0)) * (l1 - l0);
  }
  return 0;
}

/**
 * A 30 mm round striking a hull.
 *
 * The single most important piece of feedback the gun has: it is the difference between
 * "I am shooting at it" and "I am hitting it", and the splashes either side of a hull
 * say the same thing by omission. Unlike the water hits this has no range gate — a hit
 * at four kilometres still deserves to be heard, just quietly and late.
 */
export const HULL_HIT_SOUND = {
  url: '/audio/hull-hit.wav',
  /**
   * Adam: far too loud for the range. This is a hand-sized metallic crack, not an
   * explosion, and the first cut treated it like a report that carries.
   */
  gain: 0.45,
  /**
   * Reference distance for the falloff, with an exponent steeper than inverse. Halved
   * from 900 m and steepened together: a strike at 2 km should be a faint tick under
   * the sea, and at 4 km barely there at all.
   */
  referenceRange: 450,
  falloffExponent: 1.15,
  /**
   * Random scatter applied to each strike's arrival, seconds.
   *
   * The reason a burst read as a single loud clang rather than several. Rounds landing
   * in the same tick share a distance, so they shared a travel delay to the millisecond
   * and fired coincidently — three identical samples in phase are one sample nearly ten
   * decibels louder, which is exactly the two things Adam heard. Scattering them across
   * a tenth of a second turns the same hits into the rattle they should be.
   */
  arrivalScatter: 0.11,
  /** Playback-rate jitter, ± this fraction — identical repeats comb-filter. */
  pitchJitter: 0.09,
  /**
   * Concurrent hit voices. Sustained fire on a close hull lands five rounds a second
   * against a one-second sample; past this the oldest is faded rather than the newest
   * dropped, so what you hear is always the rounds that just struck.
   */
  maxVoices: 8,
} as const;

/** Rounds per crate delivered by supply ships. */
export const AMMO_CRATE = 600;
